



































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { firestore } from "firebase";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import DatePickerComponent from "@/components/common/DatePicker.vue";
import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";
import { MIRA_LATE_EVENTS, MiraException, MiraLateEvent } from "@/data/models/MiraExceptionAdministration";
import { saveMiraException } from "@/data/repository/MiraExceptionAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { clientLabel } from "@/utils/MiraCompanyAdministration";
import {
  canSaveExceptionForm,
  dateOfDay,
  dayOf,
  exceptionErrorMessage,
  exceptionFieldError,
  exceptionFormChanged,
  MiraExceptionForm,
  MiraExceptionPrefill,
  toExceptionForm,
  toExceptionSave,
  toNewExceptionForm,
} from "@/utils/MiraExceptionAdministration";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/** What `show()` resolves with. */
export type MiraExceptionDialogResult = MiraException | "CHANGED_MEANWHILE" | null;

type ExceptionField = "clientId" | "event" | "reason";

/**
 * Shows, edits or creates one Mira-Ausnahme (Hans, Track #2610040 R8): a Mandant recorded an event late
 * between two days, so Mira leaves the durations built on it out and says how many on the tile. IRES
 * Cloud is the authority (`saveMiraExceptionForAdministration`).
 *
 * `show()` resolves with the saved entry, `CHANGED_MEANWHILE` when IRES Cloud refused because the entry
 * changed since it was loaded (the caller reloads), or null when closed without saving. A new entry can
 * start pre-filled from one of Mira's suggestions (ÜBERNEHMEN, R8 Phase 3); it then counts as changed, so
 * ABBRECHEN asks before the reviewer's start is thrown away.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "date-picker": DatePickerComponent,
  },
})
export default class MiraExceptionDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  isEditMode = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: MiraException | null = null;
  form: MiraExceptionForm = toExceptionForm(null);
  clients: ClientAdministrationRow[] = [];
  /** A field shows its error once it was left, never on an untouched new form or while typing. */
  touched: Record<ExceptionField, boolean> = { clientId: false, event: false, reason: false };

  private resolve: ((result: MiraExceptionDialogResult) => void) | null = null;

  get isNew(): boolean {
    return this.stored === null;
  }

  get hasChanged(): boolean {
    return exceptionFormChanged(this.stored, this.form);
  }

  get canSave(): boolean {
    return canSaveExceptionForm(this.form);
  }

  /** Every Mandant by name; a stored one missing from the list (deleted since) still shows. */
  get clientItems(): Array<{ value: string; text: string }> {
    const byId = new Map(this.clients.map((client) => [client.id, client]));
    const ids = [...new Set([...this.clients.map((client) => client.id), ...(this.form.clientId ? [this.form.clientId] : [])])];
    return ids.map((id) => ({ value: id, text: clientLabel(byId.get(id), id) })).sort((a, b) => a.text.localeCompare(b.text, "de"));
  }

  get eventItems(): Array<{ value: MiraLateEvent; text: string }> {
    return MIRA_LATE_EVENTS.map((event) => ({ value: event, text: this.$t(`admin.miraExceptions.events.${event}`).toString() }));
  }

  /** Which figures the chosen event leaves out, so the person sees what the entry does. */
  get eventHint(): string {
    return this.form.event ? this.$t(`admin.miraExceptions.eventFigures.${this.form.event}`).toString() : "";
  }

  get fromTimestamp(): firestore.Timestamp | null {
    const date = dateOfDay(this.form.from);
    return date ? firestore.Timestamp.fromDate(date) : null;
  }

  set fromTimestamp(value: firestore.Timestamp | null) {
    this.form.from = value ? dayOf(value.toDate()) : "";
  }

  get toTimestamp(): firestore.Timestamp | null {
    const date = dateOfDay(this.form.to);
    return date ? firestore.Timestamp.fromDate(date) : null;
  }

  set toTimestamp(value: firestore.Timestamp | null) {
    this.form.to = value ? dayOf(value.toDate()) : "";
  }

  /** The range's message, once both days are chosen and in the wrong order. */
  get rangeError(): string {
    if (!this.isEditMode || !this.form.from || !this.form.to) return "";
    return exceptionFieldError("range", this.form) ? this.$t("admin.miraExceptions.fieldErrors.range").toString() : "";
  }

  /** A field's message in edit mode only, and only once the field was touched. */
  errorFor(field: ExceptionField): string[] {
    if (!this.isEditMode || !this.touched[field]) return [];
    const error = exceptionFieldError(field, this.form);
    return error ? [this.$t(`admin.miraExceptions.fieldErrors.${error}`).toString()] : [];
  }

  touch(field: ExceptionField) {
    this.touched[field] = true;
  }

  formatDate(millis: number): string {
    return formatDateToDayMonthYear(new Date(millis)) ?? "–";
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  /**
   * @param entry - the entry to show, or null to create one.
   * @param clients - every Mandant, for the picker's names.
   * @param prefill - for a new entry: the Mandant, event and days of a taken-over suggestion.
   */
  show(entry: MiraException | null, clients: ClientAdministrationRow[], prefill: MiraExceptionPrefill | null = null): Promise<MiraExceptionDialogResult> {
    this.stored = entry ? { ...entry } : null;
    this.form = entry ? toExceptionForm(entry) : toNewExceptionForm(prefill);
    this.clients = clients;
    this.touched = { clientId: false, event: false, reason: false };
    this.isEditMode = entry === null;
    this.baseDialog.show({
      title: this.titleText(),
      // No BEARBEITEN for the shared IRES admin login, which IRES Cloud refuses (#2610071).
      positiveButtonText: this.isEditMode || this.mayEdit
        ? this.$t(this.isEditMode ? "common.save" : "settings.buttons.change").toString()
        : null,
      // A pre-filled new form is changed from the start, and the watcher below only sees a change.
      negativeButtonText: this.$t(this.isEditMode && this.hasChanged ? "common.abort" : "common.close").toString(),
      maxWidth: 650,
      persistent: this.isEditMode,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  private titleText(): string {
    if (this.isNew) return this.$t("admin.miraExceptions.dialog.createTitle").toString();
    return this.$t(this.isEditMode ? "admin.miraExceptions.dialog.editTitle" : "admin.miraExceptions.dialog.viewTitle").toString();
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    if (this.isEditMode) {
      this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
    }
  }

  /** BEARBEITEN in view mode, SICHERN in edit mode. */
  onPositive() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.baseDialog.title = this.titleText();
      this.baseDialog.positiveButtonText = this.$t("common.save").toString();
      this.baseDialog.persistent = true;
      return;
    }
    this.onSave();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.isEditMode || !this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged || !this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await saveMiraException(toExceptionSave(this.stored, this.form));
      SnackbarModule.showSuccess({ key: "admin.miraExceptions.saved" });
      this.close(saved);
    } catch (error) {
      const message = exceptionErrorMessage(error);
      SnackbarModule.showError(message);
      // Someone changed the entry meanwhile: close, so the list reloads and the user starts from the new values.
      if (message.key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraExceptionDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: MiraExceptionDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
